/*
 * Rich text for message bodies.
 *
 * Formatted bodies are stored as a Quill Delta JSON string in `textDelta`; `text` always
 * holds the plain version for old apps, push notifications, previews and search. The server
 * sanitizes and re-derives `text` (backend/src/utils/rich-text.js), but stored data is still
 * treated as untrusted here: every insert is escaped and only palette classes are emitted.
 * Keep the palette in sync with the backend, the functions, iOS and Android.
 */

export const RICH_TEXT_COLORS = ['#d32f2f', '#bf360c', '#2e7d32', '#1565c0', '#6a1b9a', '#616161'];
export const RICH_BACKGROUND_COLORS = ['#fff59d', '#c8e6c9', '#bbdefb', '#f8bbd0', '#ffe0b2'];
export const RICH_TEXT_FORMATS = ['bold', 'color', 'background', 'list'];

const BULLET_PREFIX = '• ';
// Same pattern as the newline pipe uses for plain bodies.
// tslint:disable-next-line:max-line-length
const URL_PATTERN = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;

interface RichSegment {
    text: string;
    bold?: boolean;
    color?: string;
    background?: string;
}

interface RichLine {
    segments: RichSegment[];
    list?: string;
}

function paletteColor(value: any, palette: string[]): string {
    if (typeof value !== 'string') {
        return undefined;
    }
    const color = value.trim().toLowerCase();
    return palette.indexOf(color) >= 0 ? color : undefined;
}

// Drops control characters except tab and newline, plus U+FEFF (Quill's cursor marker).
function cleanText(value: string): string {
    let result = '';
    for (let i = 0; i < value.length; i++) {
        const code = value.charCodeAt(i);
        if (code === 9 || code === 10 || (code >= 32 && code !== 127 && code !== 0xFEFF)) {
            result += value[i];
        }
    }
    return result;
}

function parseOps(value: any): any[] {
    let delta = value;
    if (typeof delta === 'string') {
        if (!delta.trim()) {
            return null;
        }
        try {
            delta = JSON.parse(delta);
        } catch (e) {
            return null;
        }
    }
    if (Array.isArray(delta)) {
        return delta;
    }
    return delta && Array.isArray(delta.ops) ? delta.ops : null;
}

function segmentFor(text: string, attributes: any): RichSegment {
    const segment: RichSegment = { text };
    if (attributes && typeof attributes === 'object') {
        if (attributes.bold === true) {
            segment.bold = true;
        }
        segment.color = paletteColor(attributes.color, RICH_TEXT_COLORS);
        segment.background = paletteColor(attributes.background, RICH_BACKGROUND_COLORS);
    }
    return segment;
}

function listFor(attributes: any): string {
    if (!attributes || typeof attributes !== 'object') {
        return undefined;
    }
    if (attributes.list === 'ordered') {
        return 'ordered';
    }
    if (attributes.list === 'bullet' || attributes.list === 'checked' || attributes.list === 'unchecked') {
        return 'bullet';
    }
    return undefined;
}

function lineText(line: RichLine): string {
    return line.segments.map(segment => segment.text).join('');
}

// Whitelisted line model of a Delta, or null when it is invalid or has no visible text.
function toLines(value: any): RichLine[] {
    const ops = parseOps(value);
    if (!ops) {
        return null;
    }
    const lines: RichLine[] = [];
    let segments: RichSegment[] = [];
    ops.forEach(op => {
        if (!op || typeof op !== 'object' || typeof op.insert !== 'string') {
            return;
        }
        const parts = cleanText(op.insert).split('\n');
        parts.forEach((part, index) => {
            if (part) {
                segments.push(segmentFor(part, op.attributes));
            }
            if (index < parts.length - 1) {
                lines.push({ segments, list: listFor(op.attributes) });
                segments = [];
            }
        });
    });
    if (segments.length) {
        lines.push({ segments });
    }
    while (lines.length && !lines[lines.length - 1].list && !lineText(lines[lines.length - 1]).trim()) {
        lines.pop();
    }
    return lines.map(lineText).join('\n').trim() ? lines : null;
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// Escapes text and turns URLs into links, like the newline pipe does for plain bodies.
function linkify(text: string): string {
    const pattern = new RegExp(URL_PATTERN.source, 'g');
    let html = '';
    let last = 0;
    for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
        const url = match[0];
        const href = url.indexOf('http') === 0 ? url : 'https://' + url;
        html += escapeHtml(text.substring(last, match.index));
        html += '<a href="' + escapeHtml(href) + '" target="_blank" rel="noopener">' + escapeHtml(url) + '</a>';
        last = match.index + url.length;
    }
    return html + escapeHtml(text.substring(last));
}

function segmentHtml(segment: RichSegment): string {
    let html = linkify(segment.text);
    if (segment.bold) {
        html = '<strong>' + html + '</strong>';
    }
    const classes = [];
    if (segment.color) {
        classes.push('rt-fg-' + segment.color.substring(1));
    }
    if (segment.background) {
        classes.push('rt-bg-' + segment.background.substring(1));
    }
    return classes.length ? '<span class="' + classes.join(' ') + '">' + html + '</span>' : html;
}

/*
 * HTML for a stored Delta, or '' when it is missing, invalid or empty (callers then show
 * `text`). Only escaped text, <p>, <br>, <strong>, <ul>, <ol>, <li>, <a> and our own
 * rt-* classes are produced; Angular's sanitizer still runs on top of it.
 */
export function deltaToHtml(value: any): string {
    const lines = toLines(value);
    if (!lines) {
        return '';
    }
    let html = '';
    let openList = '';
    lines.forEach(line => {
        const tag = line.list === 'ordered' ? 'ol' : line.list === 'bullet' ? 'ul' : '';
        if (tag !== openList) {
            html += (openList ? '</' + openList + '>' : '') + (tag ? '<' + tag + '>' : '');
            openList = tag;
        }
        const content = line.segments.map(segmentHtml).join('') || '<br>';
        html += tag ? '<li>' + content + '</li>' : '<p>' + content + '</p>';
    });
    return html + (openList ? '</' + openList + '>' : '');
}

// Plain text of a Delta: bullets as "• ", ordered items as "1. " (same rules as the server).
export function deltaToPlain(value: any): string {
    const lines = toLines(value);
    if (!lines) {
        return '';
    }
    let number = 0;
    return lines.map(line => {
        if (line.list === 'ordered') {
            number += 1;
            return number + '. ' + lineText(line);
        }
        number = 0;
        return (line.list === 'bullet' ? BULLET_PREFIX : '') + lineText(line);
    }).join('\n').replace(/\s+$/, '');
}

// Delta JSON for plain text (drafts, premade messages and templates saved before rich text).
export function plainToDelta(text: string): string {
    if (!text) {
        return '';
    }
    const insert = cleanText(text.replace(/\r\n?/g, '\n'));
    return JSON.stringify({ ops: [{ insert: insert.charAt(insert.length - 1) === '\n' ? insert : insert + '\n' }] });
}

// ngx-quill value accessors: the form model holds the Delta JSON string ('' when empty).
export function richTextValueGetter(quillEditor: any): string {
    const text = quillEditor.getText();
    return text && text.trim() ? JSON.stringify(quillEditor.getContents()) : '';
}

export function richTextValueSetter(quillEditor: any, value: any): any {
    const ops = parseOps(value);
    return { ops: ops || [{ insert: '\n' }] };
}

// Pasted colors outside the palette (Word, Outlook, web pages) are dropped in the editor.
function keepPaletteColors(node: any, delta: any): any {
    delta.ops = delta.ops.map(op => {
        if (!op.attributes) {
            return op;
        }
        const attributes = Object.assign({}, op.attributes);
        if (attributes.color && !paletteColor(attributes.color, RICH_TEXT_COLORS)) {
            delete attributes.color;
        }
        if (attributes.background && !paletteColor(attributes.background, RICH_BACKGROUND_COLORS)) {
            delete attributes.background;
        }
        const result = Object.assign({}, op, { attributes });
        if (!Object.keys(attributes).length) {
            delete result.attributes;
        }
        return result;
    });
    return delta;
}

// Icons for the two color pickers in the same stroke style as Quill's other icons. The bar under
// each icon carries ql-color-label, so Quill fills it with the color of the current selection.
const TEXT_COLOR_ICON = '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="5 11.5 9 2.5 13 11.5"></polyline>'
    + '<line class="ql-stroke" x1="6.5" x2="11.5" y1="8.5" y2="8.5"></line>'
    + '<rect class="ql-color-label rt-color-bar" x="3" y="14" width="12" height="3" rx="1.5"></rect></svg>';
const HIGHLIGHT_ICON = '<svg viewBox="0 0 18 18"><path class="ql-stroke" d="M11 2.5l3.5 3.5-5 5-3.5-3.5z"></path>'
    + '<path class="ql-stroke" d="M6 7.5l-1.5 3.5 1 1 3.5-1.5"></path>'
    + '<rect class="ql-color-label rt-color-bar" x="3" y="14" width="12" height="3" rx="1.5"></rect></svg>';

// (onEditorCreated): swaps Quill's default color and background icons, which come from two
// different icon styles, for this editor only (categories, notes and help keep the defaults).
export function setupRichTextToolbar(quillEditor: any): void {
    const pickers = quillEditor && quillEditor.theme && quillEditor.theme.pickers || [];
    pickers.forEach(picker => {
        const classes = picker.select.classList;
        const icon = classes.contains('ql-color') ? TEXT_COLOR_ICON : classes.contains('ql-background') ? HIGHLIGHT_ICON : '';
        if (icon) {
            picker.label.innerHTML = icon;
            picker.update();
        }
    });
}

export const RICH_TEXT_MODULES = {
    toolbar: [
        ['bold'],
        [{ color: (<any[]>[false]).concat(RICH_TEXT_COLORS) }, { background: (<any[]>[false]).concat(RICH_BACKGROUND_COLORS) }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
    ],
    clipboard: {
        matchers: [[1 /* Node.ELEMENT_NODE */, keepPaletteColors]],
    },
};



// WEBPACK FOOTER //
// ./src/app/shared/rich-text.ts